import { useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CurrencyInput } from "@/components/currency-input";
import {
  ArrowLeft,
  Pencil,
  X,
  Loader2,
  Check,
  Circle,
  Clock,
  SkipForward,
  ChevronDown,
  ChevronRight,
  Upload,
  FileText,
  Download,
  Trash2,
  Phone,
  Mail,
  MessageSquare,
  Users,
  Send,
  Plus,
  Building2,
  CalendarDays,
  Banknote,
  TrendingUp,
  TrendingDown,
  Receipt,
  Bell,
  Link2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDate, formatDateShort } from "@/lib/utils";
import { adminFetch } from "@/lib/api";
import { useFetch } from "@/hooks/use-fetch";
import { PageLoading } from "@/components/loading";
import { ErrorMessage } from "@/components/error-message";
import {
  PROGRAM_LABELS,
  PROJECT_STATUS,
  STAGE_STATUS,
  NOTE_TYPES,
} from "@/lib/constants";

// ─── Types ──────────────────────────────────────

interface Stage {
  id: number;
  stage_name: string;
  stage_order: number;
  status: string;
  notes: string;
  deadline: string | null;
}

interface Note {
  id: number;
  note_type: string;
  content: string;
  is_internal: boolean;
  author_name: string;
  created_at: string;
}

interface Document {
  id: number;
  file_name: string;
  file_type: string;
  file_size: number;
  category: string;
  description: string;
  download_url: string;
  created_at: string;
}

interface Transaction {
  id: number;
  transaction_type: string;
  amount: number;
  description: string;
  transaction_date: string;
  created_at: string;
}

interface Project {
  id: number;
  title: string;
  description: string;
  program_type: string;
  status: string;
  city: string;
  nace_code: string;
  is_osb: boolean;
  investment_amount: number;
  consulting_fee: number;
  client_id: number;
  client_name: string;
  assigned_to: number;
  assigned_to_name: string;
  call_period_id?: number;
  created_at: string;
  updated_at: string;
  stages?: Stage[];
  recent_notes?: Note[];
  document_count?: number;
}

interface ProjectResponse {
  data: Project;
}

interface StagesResponse {
  data: Stage[];
}

interface NotesResponse {
  data: Note[];
}

interface DocumentsResponse {
  data: Document[];
}

interface FinancialResponse {
  data: Transaction[];
  summary: Record<string, number>;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface User {
  id: number;
  name: string;
}

interface UsersResponse {
  data: User[];
}

interface ActiveCallPeriod {
  id: number;
  title: string;
  institution: string;
  submission_deadline: string;
  eligible_cities: string;
}

interface ActiveCallPeriodsResponse {
  data: ActiveCallPeriod[];
}

type Tab = "stages" | "documents" | "notes" | "financial";

const NOTE_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  note: MessageSquare,
  phone: Phone,
  email: Mail,
  meeting: Users,
};

const TRANSACTION_TYPES: Record<string, { label: string }> = {
  payment: { label: "Tahsilat" },
  invoice: { label: "Fatura" },
  expense: { label: "Masraf" },
};

// ─── Main Component ─────────────────────────────

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>("stages");
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { data, loading, error, refetch } = useFetch<ProjectResponse>(
    `/projects/${id}`
  );

  const { data: usersData } = useFetch<UsersResponse>("/users");

  async function handleDeleteProject() {
    if (!id) return;
    setDeleting(true);
    try {
      await adminFetch(`/projects/${id}`, { method: "DELETE" });
      navigate("/projects");
    } catch {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  }

  if (loading) return <PageLoading />;
  if (error) return <ErrorMessage message={error} onRetry={refetch} />;
  if (!data) return null;

  const project = data.data;
  const statusInfo = PROJECT_STATUS[project.status] || {
    label: project.status,
    color: "text-muted-foreground",
  };

  const tabs: { key: Tab; label: string }[] = [
    { key: "stages", label: "Aşamalar" },
    { key: "documents", label: "Belgeler" },
    { key: "notes", label: "Notlar" },
    { key: "financial", label: "Finansal" },
  ];

  return (
    <div className="space-y-6">
      {/* Back button */}
      <button
        onClick={() => navigate("/projects")}
        className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Projelere Dön
      </button>

      {/* Header Card */}
      <div className="rounded-xl border border-border bg-card p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-xl font-semibold text-foreground">
                {project.title}
              </h1>
              <span
                className={cn(
                  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium",
                  statusInfo.color
                )}
              >
                {statusInfo.label}
              </span>
              <span className="inline-flex items-center rounded-full border border-border px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                {PROGRAM_LABELS[project.program_type] || project.program_type}
              </span>
            </div>
            {project.description && (
              <p className="mt-2 text-sm text-muted-foreground">
                {project.description}
              </p>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <button
                onClick={() => navigate(`/clients/${project.client_id}`)}
                className="inline-flex items-center gap-1.5 transition-colors hover:text-accent"
              >
                <Building2 className="h-3.5 w-3.5" />
                {project.client_name}
              </button>
              {project.assigned_to_name && (
                <span className="inline-flex items-center gap-1.5">
                  <Users className="h-3.5 w-3.5" />
                  {project.assigned_to_name}
                </span>
              )}
              <span className="inline-flex items-center gap-1.5">
                <CalendarDays className="h-3.5 w-3.5" />
                {formatDateShort(project.created_at)}
              </span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-3">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowReminderModal(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              >
                <Bell className="h-3.5 w-3.5" />
                Hatırlatıcı
              </button>
              <button
                onClick={() => setShowEditModal(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              >
                <Pencil className="h-3.5 w-3.5" />
                Düzenle
              </button>
            </div>
            <div className="text-right text-sm">
              {project.investment_amount > 0 && (
                <div className="text-muted-foreground">
                  <span className="font-medium text-foreground">
                    {Number(project.investment_amount).toLocaleString("tr-TR")} TL
                  </span>
                  <span className="ml-1">Yatırım</span>
                </div>
              )}
              {project.consulting_fee > 0 && (
                <div className="text-muted-foreground">
                  <span className="font-medium text-foreground">
                    {Number(project.consulting_fee).toLocaleString("tr-TR")} TL
                  </span>
                  <span className="ml-1">Danışmanlık</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="-mb-px flex gap-6" aria-label="Proje sekmeleri">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                "whitespace-nowrap border-b-2 pb-3 text-sm font-medium transition-colors",
                activeTab === tab.key
                  ? "border-accent text-accent"
                  : "border-transparent text-muted-foreground hover:border-border hover:text-foreground"
              )}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "stages" && <StagesSection projectId={id!} refetchProject={refetch} />}
      {activeTab === "documents" && <DocumentsSection projectId={id!} />}
      {activeTab === "notes" && <NotesSection projectId={id!} />}
      {activeTab === "financial" && <FinancialSection projectId={id!} consultingFee={project.consulting_fee} refetchProject={refetch} />}

      {/* Reminder Modal */}
      {showReminderModal && (
        <ReminderModal
          projectId={id!}
          users={usersData?.data || []}
          onClose={() => setShowReminderModal(false)}
        />
      )}

      {/* Edit Project Modal */}
      {showEditModal && (
        <EditProjectModal
          project={project}
          onClose={() => setShowEditModal(false)}
          onSaved={() => { setShowEditModal(false); refetch(); }}
        />
      )}

      {/* Delete Project */}
      <div className="rounded-xl border border-destructive/30 bg-card p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-destructive">Tehlikeli Alan</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Bu projeyi kalıcı olarak silmek için aşağıdaki butona tıklayın.
            </p>
          </div>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-2 text-sm font-medium text-destructive transition-colors hover:bg-destructive/20"
          >
            Projeyi Sil
          </button>
        </div>
      </div>

      {/* Delete Confirm Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowDeleteConfirm(false)} />
          <div className="relative z-10 w-full max-w-sm rounded-xl border border-border/60 bg-card p-6 shadow-2xl ring-1 ring-white/5">
            <h2 className="text-lg font-semibold text-foreground">Projeyi Sil</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              <strong>{project.title}</strong> projesini silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              >
                İptal
              </button>
              <button
                onClick={handleDeleteProject}
                disabled={deleting}
                className={cn(
                  "inline-flex items-center gap-2 rounded-lg bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground transition-colors hover:bg-destructive/90",
                  deleting && "cursor-not-allowed opacity-50"
                )}
              >
                {deleting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Evet, Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Stages Section ─────────────────────────────

function StagesSection({
  projectId,
  refetchProject,
}: {
  projectId: string;
  refetchProject: () => void;
}) {
  const [expandedStage, setExpandedStage] = useState<number | null>(null);
  const [updatingStage, setUpdatingStage] = useState<number | null>(null);

  const { data, loading, error, refetch } = useFetch<StagesResponse>(
    `/projects/${projectId}/stages`,
    [projectId]
  );

  async function handleStageUpdate(
    stageId: number,
    update: { status?: string; notes?: string; deadline?: string }
  ) {
    setUpdatingStage(stageId);
    try {
      await adminFetch(`/projects/${projectId}/stages/${stageId}`, {
        method: "PATCH",
        body: JSON.stringify(update),
      });
      refetch();
      refetchProject();
    } catch {
      // Error handled via UI
    } finally {
      setUpdatingStage(null);
    }
  }

  if (loading) return <PageLoading />;
  if (error) return <ErrorMessage message={error} onRetry={refetch} />;
  if (!data || data.data.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-card px-6 py-12 text-center text-sm text-muted-foreground">
        Bu proje için henüz aşama tanımlanmamış.
      </div>
    );
  }

  const stages = data.data;

  return (
    <div className="relative space-y-0">
      {/* Vertical timeline line */}
      <div className="absolute left-5 top-4 bottom-4 w-px bg-border" />

      {stages.map((stage, idx) => {
        const stageStatus = STAGE_STATUS[stage.status] || {
          label: stage.status,
          color: "text-muted-foreground",
        };
        const isExpanded = expandedStage === stage.id;
        const isUpdating = updatingStage === stage.id;

        return (
          <div key={stage.id} className="relative pl-12 pb-6 last:pb-0">
            {/* Timeline dot */}
            <div className="absolute left-3 top-1 flex h-5 w-5 items-center justify-center">
              {stage.status === "completed" ? (
                <div className="flex h-5 w-5 items-center justify-center rounded-full bg-green-500/20">
                  <Check className="h-3 w-3 text-green-400" />
                </div>
              ) : stage.status === "active" ? (
                <div className="flex h-5 w-5 items-center justify-center rounded-full bg-accent/20">
                  <div className="h-2.5 w-2.5 animate-pulse rounded-full bg-accent" />
                </div>
              ) : stage.status === "skipped" ? (
                <div className="flex h-5 w-5 items-center justify-center rounded-full bg-muted-foreground/10">
                  <SkipForward className="h-3 w-3 text-muted-foreground/50" />
                </div>
              ) : (
                <div className="flex h-5 w-5 items-center justify-center rounded-full bg-secondary">
                  <Circle className="h-3 w-3 text-muted-foreground" />
                </div>
              )}
            </div>

            {/* Stage card */}
            <div
              className={cn(
                "rounded-lg border bg-card transition-colors",
                stage.status === "active"
                  ? "border-accent/30"
                  : "border-border",
                isExpanded && "ring-1 ring-border"
              )}
            >
              <button
                onClick={() =>
                  setExpandedStage(isExpanded ? null : stage.id)
                }
                className="flex w-full items-center justify-between p-4 text-left"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xs font-medium text-muted-foreground">
                    {idx + 1}
                  </span>
                  <span className="text-sm font-medium text-foreground">
                    {stage.stage_name}
                  </span>
                  <span className={cn("text-xs font-medium", stageStatus.color)}>
                    {stageStatus.label}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  {stage.deadline && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {formatDateShort(stage.deadline)}
                    </span>
                  )}
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
              </button>

              {isExpanded && (
                <StageExpandedPanel
                  stage={stage}
                  isUpdating={isUpdating}
                  onUpdate={(update) =>
                    handleStageUpdate(stage.id, update)
                  }
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StageExpandedPanel({
  stage,
  isUpdating,
  onUpdate,
}: {
  stage: Stage;
  isUpdating: boolean;
  onUpdate: (update: {
    status?: string;
    notes?: string;
    deadline?: string;
  }) => void;
}) {
  const [notes, setNotes] = useState(stage.notes || "");
  const [deadline, setDeadline] = useState(
    stage.deadline ? stage.deadline.split("T")[0] : ""
  );

  return (
    <div className="border-t border-border p-4 space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
            Durum
          </label>
          <div className="flex flex-wrap gap-2">
            {Object.entries(STAGE_STATUS).map(([key, { label }]) => (
              <button
                key={key}
                disabled={isUpdating || stage.status === key}
                onClick={() => onUpdate({ status: key })}
                className={cn(
                  "rounded-md border px-3 py-1.5 text-xs font-medium transition-colors",
                  stage.status === key
                    ? "border-accent/30 bg-accent/10 text-accent"
                    : "border-border text-muted-foreground hover:bg-secondary hover:text-foreground",
                  isUpdating && "cursor-not-allowed opacity-50"
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
            Son Tarih
          </label>
          <div className="flex gap-2">
            <input
              type="date"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
              className="h-8 flex-1 rounded-lg border border-border bg-secondary px-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            />
            <button
              disabled={isUpdating}
              onClick={() => onUpdate({ deadline })}
              className={cn(
                "rounded-lg bg-accent px-3 py-1.5 text-xs font-medium text-accent-foreground transition-colors hover:bg-accent/90",
                isUpdating && "cursor-not-allowed opacity-50"
              )}
            >
              {isUpdating ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                "Kaydet"
              )}
            </button>
          </div>
        </div>
      </div>
      <div>
        <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
          Aşama Notu
        </label>
        <div className="flex gap-2">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="flex-1 rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          />
          <button
            disabled={isUpdating}
            onClick={() => onUpdate({ notes })}
            className={cn(
              "self-end rounded-lg bg-accent px-3 py-1.5 text-xs font-medium text-accent-foreground transition-colors hover:bg-accent/90",
              isUpdating && "cursor-not-allowed opacity-50"
            )}
          >
            {isUpdating ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              "Kaydet"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Documents Section ──────────────────────────

function DocumentsSection({ projectId }: { projectId: string }) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const { data, loading, error, refetch } = useFetch<DocumentsResponse>(
    `/projects/${projectId}/documents`,
    [projectId]
  );

  async function uploadFile(file: File) {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const API_BASE = import.meta.env.VITE_API_URL
        ? `${import.meta.env.VITE_API_URL}/api/admin`
        : "/api/admin";

      const headers: Record<string, string> = {};
      if (import.meta.env.DEV) {
        headers["X-Admin-Email"] = "abdulahcicekli@gmail.com";
      }
      const cfMatch = document.cookie.match(/CF_Authorization=([^;]+)/);
      if (cfMatch) headers["Cf-Access-Jwt-Assertion"] = cfMatch[1];

      const res = await fetch(
        `${API_BASE}/projects/${projectId}/documents/upload`,
        {
          method: "POST",
          headers,
          body: formData,
        }
      );

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json?.error?.message || "Yükleme başarısız");
      }

      refetch();
    } catch {
      // Error handled via UI
    } finally {
      setUploading(false);
    }
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) uploadFile(file);
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    e.target.value = "";
  }

  function formatFileSize(bytes: number) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  if (loading) return <PageLoading />;
  if (error) return <ErrorMessage message={error} onRetry={refetch} />;

  const documents = data?.data || [];

  return (
    <div className="space-y-4">
      {/* Upload area */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={cn(
          "flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-8 transition-colors",
          dragOver
            ? "border-accent bg-accent/5"
            : "border-border hover:border-accent/50 hover:bg-secondary/50"
        )}
      >
        {uploading ? (
          <Loader2 className="h-8 w-8 animate-spin text-accent" />
        ) : (
          <Upload className="h-8 w-8 text-muted-foreground" />
        )}
        <p className="mt-3 text-sm text-muted-foreground">
          {uploading
            ? "Yükleniyor..."
            : "Dosya yüklemek için tıklayın veya sürükleyin"}
        </p>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileSelect}
        />
      </div>

      {/* Document list */}
      {documents.length === 0 ? (
        <div className="rounded-xl border border-border bg-card px-6 py-12 text-center text-sm text-muted-foreground">
          Henüz belge yüklenmemiş.
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-card">
          <div className="divide-y divide-border">
            {documents.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center justify-between p-4"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {doc.file_name}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{formatFileSize(doc.file_size)}</span>
                      <span>-</span>
                      <span>{formatDateShort(doc.created_at)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {doc.download_url && (
                    <a
                      href={doc.download_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                    >
                      <Download className="h-4 w-4" />
                    </a>
                  )}
                  <button
                    onClick={async () => {
                      try {
                        await adminFetch(
                          `/projects/${projectId}/documents/${doc.id}`,
                          { method: "DELETE" }
                        );
                        refetch();
                      } catch {
                        // Error handled
                      }
                    }}
                    className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Notes Section ──────────────────────────────

function NotesSection({ projectId }: { projectId: string }) {
  const [noteType, setNoteType] = useState("note");
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const { data, loading, error, refetch } = useFetch<NotesResponse>(
    `/projects/${projectId}/notes`,
    [projectId]
  );

  async function handleAddNote(e: React.FormEvent) {
    e.preventDefault();
    if (!content.trim()) return;
    setSubmitting(true);
    try {
      await adminFetch(`/projects/${projectId}/notes`, {
        method: "POST",
        body: JSON.stringify({
          note_type: noteType,
          content: content.trim(),
          is_internal: true,
        }),
      });
      setContent("");
      refetch();
    } catch {
      // Error handled
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <PageLoading />;
  if (error) return <ErrorMessage message={error} onRetry={refetch} />;

  const notes = data?.data || [];

  return (
    <div className="space-y-4">
      {/* Add note form */}
      <div className="rounded-xl border border-border bg-card p-4">
        <form onSubmit={handleAddNote} className="space-y-3">
          <div className="flex items-center gap-2">
            {Object.entries(NOTE_TYPES).map(([key, { label }]) => {
              const Icon = NOTE_ICON[key] || MessageSquare;
              return (
                <button
                  type="button"
                  key={key}
                  onClick={() => setNoteType(key)}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors",
                    noteType === key
                      ? "border-accent/30 bg-accent/10 text-accent"
                      : "border-border text-muted-foreground hover:bg-secondary hover:text-foreground"
                  )}
                >
                  <Icon className="h-3 w-3" />
                  {label}
                </button>
              );
            })}
          </div>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Not ekle..."
            rows={3}
            className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          />
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={!content.trim() || submitting}
              className={cn(
                "inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-foreground transition-colors hover:bg-accent/90",
                (!content.trim() || submitting) &&
                  "cursor-not-allowed opacity-50"
              )}
            >
              {submitting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Send className="h-3.5 w-3.5" />
              )}
              Ekle
            </button>
          </div>
        </form>
      </div>

      {/* Notes timeline */}
      {notes.length === 0 ? (
        <div className="rounded-xl border border-border bg-card px-6 py-12 text-center text-sm text-muted-foreground">
          Henüz not bulunmuyor.
        </div>
      ) : (
        <div className="space-y-3">
          {notes.map((note) => {
            const Icon = NOTE_ICON[note.note_type] || MessageSquare;
            const typeLabel =
              NOTE_TYPES[note.note_type]?.label || note.note_type;
            return (
              <div
                key={note.id}
                className="rounded-xl border border-border bg-card p-4"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-secondary">
                    <Icon className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">
                        {note.author_name}
                      </span>
                      <span className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-xs">
                        {typeLabel}
                      </span>
                      <span>{formatDate(note.created_at)}</span>
                    </div>
                    <p className="mt-2 whitespace-pre-wrap text-sm text-foreground">
                      {note.content}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Reminder Modal ─────────────────────────────

function ReminderModal({
  projectId,
  users,
  onClose,
}: {
  projectId: string;
  users: User[];
  onClose: () => void;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [dueTime, setDueTime] = useState("09:00");
  const [assignedTo, setAssignedTo] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const inputClass =
    "h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent";

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !dueDate) return;
    setSubmitting(true);
    try {
      await adminFetch("/reminders", {
        method: "POST",
        body: JSON.stringify({
          project_id: Number(projectId),
          title: title.trim(),
          description: description.trim(),
          due_date: `${dueDate}T${dueTime}:00`,
          assigned_to: assignedTo ? Number(assignedTo) : null,
        }),
      });
      setSuccess(true);
      setTimeout(onClose, 1500);
    } catch {
      // Error handled
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md rounded-xl border border-border/60 bg-card p-6 shadow-2xl ring-1 ring-white/5">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">Hatırlatıcı Ekle</h2>
          <button onClick={onClose} className="rounded-lg p-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        {success ? (
          <div className="mt-6 flex flex-col items-center gap-3 py-8 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-500/10">
              <Check className="h-6 w-6 text-green-400" />
            </div>
            <p className="text-sm font-medium text-foreground">Hatırlatıcı eklendi</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-4 space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">Başlık *</label>
              <input type="text" required value={title} onChange={(e) => setTitle(e.target.value)} className={inputClass} placeholder="Hatırlatıcı başlığı" />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">Açıklama</label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent" placeholder="Detay..." />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">Tarih *</label>
                <input type="date" required value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">Saat</label>
                <input type="time" value={dueTime} onChange={(e) => setDueTime(e.target.value)} className={inputClass} />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">Atanan Kişi</label>
              <select value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} className={inputClass}>
                <option value="">Seçin...</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={onClose} className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
                İptal
              </button>
              <button type="submit" disabled={submitting} className={cn("inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-foreground transition-colors hover:bg-accent/90", submitting && "cursor-not-allowed opacity-50")}>
                {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Kaydet
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── Edit Project Modal ─────────────────────────

function EditProjectModal({
  project,
  onClose,
  onSaved,
}: {
  project: Project;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(project.title);
  const [description, setDescription] = useState(project.description || "");
  const [status, setStatus] = useState(project.status);
  const [callPeriodId, setCallPeriodId] = useState(
    project.call_period_id ? String(project.call_period_id) : ""
  );
  const [investmentAmount, setInvestmentAmount] = useState(
    project.investment_amount ? String(project.investment_amount) : ""
  );
  const [consultingFee, setConsultingFee] = useState(
    project.consulting_fee ? String(project.consulting_fee) : ""
  );
  const [assignedTo, setAssignedTo] = useState(
    project.assigned_to ? String(project.assigned_to) : ""
  );
  const [naceCode, setNaceCode] = useState(project.nace_code || "");
  const [isOsb, setIsOsb] = useState(project.is_osb || false);
  const [submitting, setSubmitting] = useState(false);

  const { data: callPeriodsData } = useFetch<ActiveCallPeriodsResponse>(
    "/call-periods/active"
  );
  const { data: usersListData } = useFetch<UsersResponse>("/users");

  const inputClass =
    "h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent";

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await adminFetch(`/projects/${project.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          title,
          description,
          status,
          call_period_id: callPeriodId ? Number(callPeriodId) : null,
          investment_amount: investmentAmount ? Number(investmentAmount) : 0,
          consulting_fee: consultingFee ? Number(consultingFee) : 0,
          assigned_to: assignedTo ? Number(assignedTo) : null,
          nace_code: naceCode || null,
          is_osb: isOsb,
        }),
      });
      onSaved();
    } catch {
      // Error handled
    } finally {
      setSubmitting(false);
    }
  }

  const callPeriods = callPeriodsData?.data || [];
  const usersList = usersListData?.data || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl border border-border/60 bg-card p-6 shadow-2xl ring-1 ring-white/5">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">Proje Düzenle</h2>
          <button onClick={onClose} className="rounded-lg p-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Proje Başlığı</label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Açıklama</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">Durum</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputClass}>
                {Object.entries(PROJECT_STATUS).map(([value, { label }]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">
                <span className="flex items-center gap-1.5">
                  <Link2 className="h-3.5 w-3.5" />
                  Çağrı Dönemi
                </span>
              </label>
              <select value={callPeriodId} onChange={(e) => setCallPeriodId(e.target.value)} className={inputClass}>
                <option value="">Seçilmedi</option>
                {callPeriods.map((cp) => (
                  <option key={cp.id} value={cp.id}>{cp.title} - {cp.institution}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <CurrencyInput
              label="Yatırım Tutarı"
              value={investmentAmount}
              onChange={setInvestmentAmount}
              placeholder="ör. 5.000.000"
            />
            <CurrencyInput
              label="Danışmanlık Ücreti"
              value={consultingFee}
              onChange={setConsultingFee}
              placeholder="ör. 150.000"
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">Danışman</label>
              <select value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} className={inputClass}>
                <option value="">Seçilmedi</option>
                {usersList.map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">NACE Kodu</label>
              <input type="text" value={naceCode} onChange={(e) => setNaceCode(e.target.value)} className={inputClass} placeholder="ör. 10.71" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="edit_is_osb"
              checked={isOsb}
              onChange={(e) => setIsOsb(e.target.checked)}
              className="h-4 w-4 rounded border-border bg-secondary text-accent focus:ring-accent"
            />
            <label htmlFor="edit_is_osb" className="text-sm font-medium text-foreground">
              OSB
            </label>
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
              İptal
            </button>
            <button type="submit" disabled={submitting} className={cn("inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-foreground transition-colors hover:bg-accent/90", submitting && "cursor-not-allowed opacity-50")}>
              {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Kaydet
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Financial Section ──────────────────────────

function FinancialSection({ projectId, consultingFee, refetchProject }: { projectId: string; consultingFee: number; refetchProject: () => void }) {
  const [showForm, setShowForm] = useState(false);
  const [txType, setTxType] = useState("payment");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [txDate, setTxDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [submitting, setSubmitting] = useState(false);

  const { data, loading, error, refetch } = useFetch<FinancialResponse>(
    `/projects/${projectId}/financial`,
    [projectId]
  );

  async function handleAddTransaction(e: React.FormEvent) {
    e.preventDefault();
    if (!amount) return;
    setSubmitting(true);
    try {
      await adminFetch(`/projects/${projectId}/financial`, {
        method: "POST",
        body: JSON.stringify({
          transaction_type: txType,
          amount: Number(amount),
          description,
          transaction_date: txDate,
        }),
      });
      setAmount("");
      setDescription("");
      setShowForm(false);
      refetch();
      refetchProject();
    } catch {
      // Error handled
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <PageLoading />;
  if (error) return <ErrorMessage message={error} onRetry={refetch} />;

  const transactions = data?.data || [];
  const summary = data?.summary || {};
  const totalCollected = summary.payment || 0;
  const fee = Number(consultingFee) || 0;
  const remaining = fee - totalCollected;

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Banknote className="h-4 w-4" />
            Danışmanlık Ücreti
          </div>
          <p className="mt-2 text-2xl font-bold text-foreground">
            {fee.toLocaleString("tr-TR")} TL
          </p>
        </div>
        <div className="rounded-xl border border-green-500/20 bg-green-500/5 p-4">
          <div className="flex items-center gap-2 text-sm text-green-400">
            <TrendingUp className="h-4 w-4" />
            Tahsil Edilen
          </div>
          <p className="mt-2 text-2xl font-bold text-green-400">
            {totalCollected.toLocaleString("tr-TR")} TL
          </p>
        </div>
        <div className="rounded-xl border border-yellow-500/20 bg-yellow-500/5 p-4">
          <div className="flex items-center gap-2 text-sm text-yellow-400">
            <TrendingDown className="h-4 w-4" />
            Kalan
          </div>
          <p className="mt-2 text-2xl font-bold text-yellow-400">
            {remaining.toLocaleString("tr-TR")} TL
          </p>
        </div>
      </div>

      {/* Add transaction */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-foreground">İşlemler</h3>
        <button
          onClick={() => setShowForm(!showForm)}
          className="inline-flex items-center gap-2 rounded-lg bg-accent px-3 py-1.5 text-sm font-medium text-accent-foreground transition-colors hover:bg-accent/90"
        >
          <Plus className="h-3.5 w-3.5" />
          Yeni İşlem
        </button>
      </div>

      {showForm && (
        <div className="rounded-xl border border-border bg-card p-4">
          <form
            onSubmit={handleAddTransaction}
            className="grid gap-4 sm:grid-cols-2"
          >
            <div>
              <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                İşlem Türü
              </label>
              <select
                value={txType}
                onChange={(e) => setTxType(e.target.value)}
                className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              >
                {Object.entries(TRANSACTION_TYPES).map(
                  ([value, { label }]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  )
                )}
              </select>
            </div>
            <div>
              <CurrencyInput
                label="Tutar *"
                value={amount}
                onChange={setAmount}
                placeholder="ör. 50.000"
                required
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                Açıklama
              </label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                Tarih
              </label>
              <input
                type="date"
                value={txDate}
                onChange={(e) => setTxDate(e.target.value)}
                className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </div>
            <div className="flex items-end gap-3 sm:col-span-2">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              >
                İptal
              </button>
              <button
                type="submit"
                disabled={submitting}
                className={cn(
                  "inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-foreground transition-colors hover:bg-accent/90",
                  submitting && "cursor-not-allowed opacity-50"
                )}
              >
                {submitting && (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                )}
                Kaydet
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Transaction list */}
      {transactions.length === 0 ? (
        <div className="rounded-xl border border-border bg-card px-6 py-12 text-center text-sm text-muted-foreground">
          Henüz işlem bulunmuyor.
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-card">
          <div className="divide-y divide-border">
            {transactions.map((tx) => {
              const typeLabel =
                TRANSACTION_TYPES[tx.transaction_type]?.label ||
                tx.transaction_type;
              return (
                <div
                  key={tx.id}
                  className="flex items-center justify-between p-4"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-secondary">
                      <Receipt className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground">
                          {typeLabel}
                        </span>
                        {tx.description && (
                          <span className="text-xs text-muted-foreground">
                            - {tx.description}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {formatDateShort(tx.transaction_date)}
                      </p>
                    </div>
                  </div>
                  <span
                    className={cn(
                      "text-sm font-semibold",
                      tx.transaction_type === "payment"
                        ? "text-green-400"
                        : tx.transaction_type === "expense"
                          ? "text-red-400"
                          : "text-foreground"
                    )}
                  >
                    {tx.transaction_type === "payment" ? "+" : "-"}
                    {Number(tx.amount).toLocaleString("tr-TR")} TL
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
